/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, FlexColumn, FlexRow, Textarea } from 'design-system';
import { isMediaSecurityEnabled } from '../../../../helpers/isMediaSecurityEnabled';
import UploadFile from './UploadFile';

function normalizeImages(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 * Question-level Add Comments / Upload File, matching Normal survey NotesAndImage
 * (allow_comment / allow_file_upload + mediaSecurity gate).
 */
export default function QuestionNotesAndImage({
  question,
  survey = null,
  note = '',
  images = [],
  onNoteChange,
  onImagesChange,
  interactive = false,
}) {
  const intl = useIntl();
  const locations = useSelector((state) => state.survey?.locations || []);
  const allowComment = Boolean(question?.allow_comment);
  const mediaSecurity = isMediaSecurityEnabled(survey, locations);
  const allowUpload = Boolean(question?.allow_file_upload) && !mediaSecurity;
  const imageFiles = normalizeImages(images);
  const [showNote, setShowNote] = useState(Boolean(String(note || '').trim()));
  const [showImage, setShowImage] = useState(imageFiles.length > 0);

  // Reset open/closed UI when navigating to a different question.
  useEffect(() => {
    setShowNote(Boolean(String(note || '').trim()));
    setShowImage(normalizeImages(images).length > 0);
  }, [question?._id]);

  useEffect(() => {
    if (String(note || '').trim()) {
      setShowNote(true);
    }
  }, [note]);

  useEffect(() => {
    if (allowUpload && imageFiles.length) {
      setShowImage(true);
    }
  }, [allowUpload, imageFiles.length]);

  if (!allowComment && !allowUpload) {
    return null;
  }

  return (
    <FlexColumn
      align="stretch"
      fullWidth
      className="cc-preview-notes-and-image"
      gap="var(--ds-space-4)"
    >
      <FlexRow
        wrap
        align="center"
        fullWidth
        gap="var(--ds-space-3)"
        className="cc-preview-notes-and-image__actions"
      >
        {allowComment ? (
          <Button
            type="button"
            variant="secondary"
            size="base"
            disabled={!interactive}
            onClick={() => interactive && setShowNote((prev) => !prev)}
          >
            {intl.formatMessage({ id: 'add-comments' })}
          </Button>
        ) : null}
        {allowUpload ? (
          <Button
            type="button"
            variant="secondary"
            size="base"
            disabled={!interactive}
            onClick={() => interactive && setShowImage((prev) => !prev)}
          >
            {intl.formatMessage({ id: 'upload-file' })}
          </Button>
        ) : null}
      </FlexRow>

      {allowComment && showNote ? (
        <Textarea
          rows={3}
          className="cc-preview-notes-and-image__note"
          placeholder={intl.formatMessage({ id: 'enter-your-comment' })}
          value={note ?? ''}
          readOnly={!interactive}
          onChange={(event) => onNoteChange?.(event.target.value)}
        />
      ) : null}

      {allowUpload && showImage ? (
        <FlexColumn
          align="stretch"
          fullWidth
          className="cc-preview-notes-and-image__upload"
        >
          <UploadFile
            preview
            value={imageFiles}
            onChange={(next) => onImagesChange?.(normalizeImages(next))}
            interactive={interactive}
          />
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );
}

QuestionNotesAndImage.propTypes = {
  question: PropTypes.shape({
    allow_comment: PropTypes.bool,
    allow_file_upload: PropTypes.bool,
  }).isRequired,
  survey: PropTypes.shape({
    mediaSecurity: PropTypes.bool,
    survey_without_login: PropTypes.shape({
      mediaSecurity: PropTypes.bool,
    }),
  }),
  note: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({})),
  onNoteChange: PropTypes.func,
  onImagesChange: PropTypes.func,
  interactive: PropTypes.bool,
};
